import { memo } from 'react';
import { Link } from 'react-router-dom';
import type { CollectionCard as Card } from '../../lib/types';
import { TypeChip } from '../ui';
import { genderSuffix } from '../../lib/format';

/**
 * One Pokemon in the Collection.
 *
 * The old card was a sprite, a name, a level and two type chips — four facts,
 * identical on every Houndour a breeder owns, which made a page of two hundred
 * a wall of interchangeable cream boxes. This one is built around a *hierarchy*
 * rather than around adding fields: identity and sprite first, then the two
 * things that separate two of the same species (nature and size) plus a genes
 * summary, then **one** highlight chosen by the server, then where it lives.
 *
 * Everything else — exact stats, parents, ribbons, origin — stays on the
 * Pokemon's own page, which is what stops the card from becoming a form.
 * Clicking a card goes there, exactly as it always has.
 *
 * The primary type tints the header stripe, the sprite well and the top border,
 * and nothing else. Recolouring the whole card would trade one wall of cream
 * for one wall of noise; a stripe is enough to break the grid up and it keeps
 * the cream body that makes this look like PokeGrove.
 */

/** Status flags, in the order they read best. Only the true ones render. */
function StatusStrip({ card }: { card: Card }) {
  return (
    <div className="ccard__status">
      {card.onTeam && (
        <span className="ccard__flag ccard__flag--team" title={`My Team, slot ${card.teamPosition}`}>
          TEAM
        </span>
      )}
      {card.isBreeding && (
        <span className="ccard__flag ccard__flag--breeding" title="In the Nursery as a breeding parent">
          BREEDING
        </span>
      )}
      {/*
        Second in the strip, above every other flag but TEAM. A World Boss
        fighter cannot be released, traded, listed or bred while the fight is
        on, and this chip is the only warning a player gets before one of those
        buttons refuses — a refusal with no visible cause reads as a bug.
      */}
      {card.worldBoss && (
        <span
          className="ccard__flag ccard__flag--worldboss"
          title="Representing you against a World Boss. It cannot be traded, sold, released or bred until the fight ends."
        >
          WORLD BOSS
        </span>
      )}
      {card.bazaarPrice != null && (
        <span className="ccard__flag ccard__flag--bazaar" title={`Listed on the Bazaar for ${card.bazaarPrice}`}>
          FOR SALE
        </span>
      )}
      {card.isLocked && (
        <span className="ccard__flag ccard__flag--lock" title="Protected" aria-label="Protected">
          🔒
        </span>
      )}
    </div>
  );
}

/**
 * Genetic potential: five pips and the figure behind them.
 *
 * The pips are what you scan a page by; the percentage is what you compare two
 * Houndour with, and it costs one line rather than the row of six exact values
 * that turns a grid into a spreadsheet. The six themselves stay on the detail
 * page, where there is room to read them.
 *
 * Stat potential *only* — a Shiny with ordinary genes is still a Shiny, and
 * folding rarity into this number would tell its owner otherwise.
 */
function Potential({ stars, label, percent }: { stars: number; label: string; percent: number }) {
  return (
    <span
      className={`ccard__potential ccard__potential--${stars}`}
      title={`Genetic potential: ${label} — ${percent}% of a perfect 186 IV total`}
      aria-label={`Genetic potential ${stars} of 5, ${label}, ${percent} percent`}
    >
      {'★'.repeat(stars)}
      <span className="ccard__potential-empty">{'☆'.repeat(5 - stars)}</span>
      <span className="ccard__potential-pct">{percent}%</span>
    </span>
  );
}

export const CollectionCardTile = memo(function CollectionCardTile({
  card,
  compact = false,
  selected = false,
  selectMode = false,
  onToggleFavorite,
  onToggleSelect,
}: {
  card: Card;
  /** The dense mode: sprite, name, level, types, flags. Nothing derived. */
  compact?: boolean;
  selected?: boolean;
  /** While on, the card picks rather than navigates. */
  selectMode?: boolean;
  onToggleFavorite: (card: Card) => void;
  onToggleSelect?: (card: Card) => void;
}) {
  const primary = card.types[0] ?? 'normal';
  const gender = genderSuffix(card.displayName, card.gender);

  const classes = [
    'pxframe',
    'ccard',
    compact ? 'ccard--compact' : '',
    card.isShiny ? 'ccard--shiny' : '',
    selected ? 'ccard--selected' : '',
    card.marking ? `ccard--marked ccard--marked-${card.marking.key}` : '',
  ]
    .filter(Boolean)
    .join(' ');


  /* The card's contents, hoisted so the shell can wrap them in either an
     anchor or a button without keeping two copies of the layout. */
  const body = (
    <>
        <StatusStrip card={card} />

        <div className="ccard__art">
          {card.isShiny && (
            <span className="ccard__shiny" aria-hidden>
              ✦
            </span>
          )}
          <img className="pixel" src={card.sprite} alt="" loading="lazy" decoding="async" />
        </div>

        <div className="ccard__name">
          {card.displayName}
          {gender && (
            <span className={`ccard__gender ccard__gender--${card.gender}`} aria-label={card.gender}>
              {gender}
            </span>
          )}
        </div>

        {/* Only when the headline is a nickname — printing "Houndour" under
            "Houndour" is a line that says nothing. */}
        {card.nickname && <div className="ccard__species">{card.speciesName}</div>}

        <div className="ccard__level">
          <span className="ccard__lv">Lv. {card.level}</span>
          {!compact && (
            <span
              className="ccard__xp"
              title={card.isMaxLevel ? 'Max level' : `${Math.round(card.expRatio * 100)}% to next level`}
              aria-hidden
            >
              <span
                className="ccard__xp-fill"
                style={{ width: `${card.isMaxLevel ? 100 : Math.round(card.expRatio * 100)}%` }}
              />
            </span>
          )}
        </div>

        <div className="ccard__types">
          {card.types.map((type) => (
            <TypeChip key={type} type={type} />
          ))}
        </div>

        {!compact && (
          <>
            {/* The two facts that tell two of the same species apart, on one
                line. Nature is plain text on purpose: 25 nature colours would
                be 25 things to learn for no gain. */}
            {(card.natureName || card.size) && (
              <div className="ccard__traits">
                {card.natureName}
                {card.natureName && card.size && ' · '}
                {card.size && (
                  <span className={card.size.title ? 'ccard__size ccard__size--extreme' : 'ccard__size'}>
                    {card.size.label}
                  </span>
                )}
              </div>
            )}

            <Potential
              stars={card.potential.stars}
              label={card.potential.label}
              percent={card.potential.percent}
            />

            {/* Exactly one. The server ranks them so a Shiny never loses to
                "Gen 2" — see pickHighlight. */}
            {card.highlight && (
              <div className={`ccard__highlight ccard__highlight--${card.highlight.tone}`}>
                <span aria-hidden>{card.highlight.icon}</span> {card.highlight.label}
              </div>
            )}

            <div className="ccard__foot">
              {card.groveName ? (
                <span title={`Living in ${card.groveName}`}>🌱 {card.groveName}</span>
              ) : (
                <span className="ccard__foot--none">Unassigned</span>
              )}
              {card.ribbonCount > 0 && !(card.highlight?.tone === 'ribbon') && (
                <span title={`${card.ribbonCount} ribbons`}>🎀 {card.ribbonCount}</span>
              )}
            </div>
          </>
        )}
    </>
  );

  return (
    <div
      className={classes}
      // The accent is one custom property; every rule below reads it, so
      // retinting a type is one line in tokens.css and nothing here.
      style={{ ['--ccard-accent' as string]: `var(--type-${primary}, var(--ink-faint))` }}
    >
      <span className="ccard__stripe" aria-hidden />

      {/* Normally the whole body is one link to the Pokemon's page — the same
          destination the old card had, and a real <a>, so middle-click,
          ctrl-click and "open in new tab" all work.

          In select mode it becomes a real button rather than an anchor that
          swallows its own click: a link that does not navigate lies to the
          status bar, to ctrl-click and to a screen reader. */}
      {selectMode ? (
        <button
          type="button"
          className="ccard__body"
          onClick={() => onToggleSelect?.(card)}
          aria-pressed={selected}
          aria-label={`${card.displayName}, level ${card.level}${card.isShiny ? ', shiny' : ''}`}
        >
          {body}
        </button>
      ) : (
        <Link
          to={`/pokemon/${card.id}`}
          className="ccard__body"
          aria-label={`${card.displayName}, level ${card.level}${card.isShiny ? ', shiny' : ''}`}
        >
          {body}
        </Link>
      )}

      {/* Outside the link — an interactive control inside an anchor is invalid
          HTML, and the star must not navigate. Hidden while selecting: a second
          clickable target inside a card whose whole job is "click to pick" is a
          way to miss, and the bulk bar can favourite the selection anyway. */}
      {!selectMode && (
        <button
          type="button"
          className={`ccard__fav ${card.isFavorite ? 'is-on' : ''}`}
          onClick={() => onToggleFavorite(card)}
          title={card.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
          aria-label={card.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={card.isFavorite}
        >
          {card.isFavorite ? '★' : '☆'}
        </button>
      )}

      {/* Selection is marked on four axes at once — ring, background, this tick
          and the label below — because it precedes actions that cannot all be
          undone, and colour alone fails both a colour-blind player and a
          careless click. */}
      {selectMode && (
        <span className={`ccard__check ${selected ? 'is-on' : ''}`} aria-hidden>
          {selected ? '✔' : ''}
        </span>
      )}
      {selected && <span className="ccard__selected-label">SELECTED</span>}
    </div>
  );
});
